/**
 * LearnerLearningEnvironment — calm "Learning" environment after
 * intent selection. Routes from the intent-first home's green
 * "Learning · Continue learning" circle.
 *
 * Operator brief (May 19, 2026 — emotional foundation):
 *   "The learner should never feel overwhelmed by the amount of
 *    learning ahead. Calm, guided, progressive, emotionally safe,
 *    understandable, confidence-building.
 *
 *    Every task has TWO parts: the task itself, and awareness of
 *    surroundings while performing it. Then the learner
 *    stabilises the task → awareness improves → responsibility
 *    increases gradually → complexity increases gradually.
 *
 *    Every area progresses through FOUR levels of complexity:
 *    simple · developing · busier · advanced.
 *
 *    The lower section is NOT a module dashboard. It is calm
 *    'what's ahead' visibility — reassurance, not content
 *    overload. The AI section should feel assistive, not
 *    demanding."
 *
 * Layout (top → bottom):
 *   • Crown · "Let's keep building your skills."
 *   • Search bar (re-used from intent home)
 *   • Three intent circles (same destinations as the home)
 *   • "Each thing you learn has two tasks." — task / awareness explainer
 *   • "You have completed the task." progress glance
 *   • 4-level complexity strip
 *   • CURRENT FOCUS card (with image, progress, two CTAs, "you will" checklist)
 *   • "What you'll learn later" — calm 5-column topic visibility list
 *   • Calm reassurance footer + mountain
 *   • "How can I help you today?" — AI helper sub-cards (assistive, not demanding)
 *
 * Persistence: read-only on the localStorage onboarding answers
 * (`learner_onboarding_preferences_v1`) for future personalisation.
 * Backend wiring deferred per operator scope.
 */
import { useCallback, useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Crown,
  Search,
  SlidersHorizontal,
  Activity,
  BookOpen,
  Calendar,
  ClipboardCheck,
  Eye,
  CheckCircle2,
  BarChart3,
  Sparkles,
  PlayCircle,
  Crosshair,
  ClipboardList,
  ChevronRight,
  Mountain,
} from "lucide-react";
import UnderstandingCard from "../components/learner/UnderstandingCard";
import IntentResponseCard from "../components/learner/IntentResponseCard";
import PupilLearningQueue from "../components/learner/PupilLearningQueue";
import { classifyLearnerIntent } from "../lib/understandingIntent";
import { buildIntentResponse } from "../lib/intentResponseLayer";

const API = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "learner_onboarding_preferences_v1";

// Doctrine pin: shared with /learn entry — learner search has TWO
// intent types. Navigation routes to pages; understanding opens
// the calm UnderstandingCard. See /app/frontend/src/lib/
// understandingIntent.js for the canonical classifier.

// The calm "what's ahead" visibility list — operator-curated.
// This is reassurance, NOT content overload. Five columns wide
// on desktop; collapses to one or two columns on mobile.
const TOPICS_AHEAD = [
  ["Junctions",       "Lanes & road position", "Bay parking",       "Looking far ahead", "Independent driving"],
  ["Roundabouts",     "Dual carriageways",     "Parallel parking",  "Planning ahead",    "Longer journeys"],
  ["Crossroads",      "Country roads",         "Reverse parking",   "Proactive driving", "Fatigue awareness"],
  ["Traffic lights",  "Motorways",             "Turning in the road","Reactive driving", "Eco driving"],
  ["Give way & priority","Slip roads",         "U-turns",           "Speed management",  "Vehicle maintenance awareness"],
  ["Mirror – Signal – Manoeuvre","Emergencies","Three point turns", "Space management",  "Test preparation"],
  ["PMSL routine",    "Hazard perception",     "Hill starts",       "Risk awareness",    "Mock tests"],
  ["Meeting traffic", "Pedestrian crossings",  "Controlled stops",  "Weather awareness", "And much more…"],
  ["Overtaking",      "Cyclists & vulnerable road users","Emergency stop","Night driving", ""],
];

function flattenTopics() {
  // Transpose the rows so we render column-by-column for the
  // 5-column desktop layout. We keep the rows intact for mobile.
  const cols = [[], [], [], [], []];
  for (const row of TOPICS_AHEAD) {
    for (let i = 0; i < 5; i++) {
      const t = row[i];
      if (t) cols[i].push(t);
    }
  }
  return cols;
}

export default function LearnerLearningEnvironment() {
  const nav = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState(() => (new URLSearchParams(location.search).get("question") || "").slice(0,1000));
  const [prefs, setPrefs] = useState(null);
  const [understanding, setUnderstanding] = useState(null);
  const [intentResponse, setIntentResponse] = useState(null);
  const [learningReply, setLearningReply] = useState(null);
  const [learningBusy, setLearningBusy] = useState(false);
  const [learningError, setLearningError] = useState("");
  const [queueVersion, setQueueVersion] = useState(0);
  // Containment doctrine (May 26, 2026): the 5-column 45-item
  // "What you'll learn later" grid must NOT render by default.
  // It is reassurance-on-demand only, opened explicitly by the
  // learner via the "Show me what's ahead" reveal.
  const [topicsAheadOpen, setTopicsAheadOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setPrefs(JSON.parse(raw));
    } catch (_) { /* private mode / quota */ }
  }, []);

  const learnerName = prefs?.name?.trim() || "";

  const submitIntent = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const learnerText = query.trim();
    if (!learnerText || learningBusy) return;
    const response = buildIntentResponse(learnerText);
    const isLearningTurn = !!learningReply?.check_id
      || response.delegate === "understanding_card"
      || response.category === "unknown";
    if (!isLearningTurn) {
      setIntentResponse(response);
      return;
    }
    setLearningBusy(true);
    setLearningError("");
    try {
      const { data } = await axios.post(
        `${API}/api/learner-framework/me/learning-turn`,
        {
          message: learnerText,
          focus: learningReply?.focus || response.topic || null,
          check_id: learningReply?.check_id || null,
          question_step: learningReply?.question_step || 0,
        },
        { withCredentials: true },
      );
      setLearningReply(data);
      setQueueVersion(value => value + 1);
      setUnderstanding(null);
      setIntentResponse(null);
      setQuery("");
    } catch {
      if (learningReply?.check_id) {
        setLearningError("Your answer could not be confirmed as saved. Your words are still here. Try again, or reopen your learning to-do list.");
        return;
      }
      const cls = classifyLearnerIntent(learnerText);
      setUnderstanding({
        topic: cls.topic || response.topic || null,
        phrase: cls.phrase || response.phrase || response.raw,
      });
    } finally {
      setLearningBusy(false);
    }
  }, [query, learningBusy, learningReply]);

  const handleIntentChoice = useCallback((choice) => {
    if (!choice) return;
    if (choice.kind === "navigate" && choice.route) {
      setIntentResponse(null);
      nav(choice.route);
      return;
    }
    if (choice.kind === "open_understanding") {
      setIntentResponse(null);
      setUnderstanding({ topic: null, phrase: query });
      return;
    }
    // ask_clarify / open_inline are inline continuations — V1
    // keeps them as a no-op so the learner stays in control.
  }, [nav, query]);

  const topicCols = useMemo(flattenTopics, []);

  return (
    <div
      data-testid="learner-learning-environment"
      className="min-h-dvh no-overflow-x bg-black text-white"
    >
      <main className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 pb-32 space-y-10">

        {/* ── Header row ── */}
        <header className="flex items-start justify-between">
          <Crown
            data-testid="learning-env-crown"
            className="w-9 h-9 text-amber-400"
            strokeWidth={2.5}
          />
          <button
            type="button"
            data-testid="learning-env-style-btn"
            onClick={() => nav("/help/learner")}
            className="flex items-center gap-2 px-3 h-9 rounded-full border border-zinc-700 bg-zinc-900/60 text-xs text-zinc-300 hover:border-zinc-500 transition-colors"
          >
            <span>My learning style</span>
            <SlidersHorizontal className="w-3.5 h-3.5" />
          </button>
        </header>

        {/* ── Heading + search ── */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end">
          <div>
            <h1
              data-testid="learning-env-heading"
              className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight"
            >
              Let's keep building<br />
              <span className="text-emerald-400">your skills{learnerName ? `, ${learnerName}` : ""}.</span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-zinc-400">
              Each thing you learn has two tasks.
            </p>
            {/* Task + awareness explainer */}
            <div className="mt-5 flex items-stretch gap-3">
              <ExplainerCard
                testid="learning-env-task-explainer"
                icon={<ClipboardCheck className="w-4 h-4 text-emerald-400" />}
                title="1. Your task"
                body="The specific skill or exercise you're given."
              />
              <span className="self-center text-zinc-500 font-bold">+</span>
              <ExplainerCard
                testid="learning-env-awareness-explainer"
                icon={<Eye className="w-4 h-4 text-emerald-400" />}
                title="2. Awareness"
                body="Awareness of your surroundings."
              />
            </div>
            <div
              data-testid="learning-env-task-completed"
              className="mt-4 flex items-center gap-2 text-sm text-zinc-300"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>Practise the task and awareness together.</span>
            </div>
            <div
              data-testid="learning-env-complexity-strip"
              className="mt-5 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-3 flex items-center gap-3"
            >
              <BarChart3 className="w-5 h-5 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-zinc-300">
                  Every task you learn has 4 levels of complexity
                </p>
                <p className="text-[11px] text-zinc-500">
                  Choose the challenge that feels right today. Support is a separate choice.
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4].map((n) => (
                  <span
                    key={n}
                    data-testid={`learning-env-complexity-${n}`}
                    className={`
                      w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-extrabold
                      border border-zinc-700 text-zinc-500
                    `}
                  >
                    {n}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-stretch gap-6">
            {!learningReply && <PupilLearningQueue refreshKey={queueVersion} onActivate={() => { setQuery(""); setLearningError(""); }} />}
            {/* Search bar — re-used pattern from intent home */}
            <form
              data-testid="learning-env-search-form"
              onSubmit={submitIntent}
            >
              <div
                className="
                  flex items-center gap-2 px-4 h-14 rounded-3xl
                  bg-zinc-900/60 border border-zinc-700
                  shadow-[0_0_24px_-12px_rgba(255,255,255,0.15)]
                  focus-within:border-zinc-500
                  transition-shadow
                "
              >
                <Search className="w-5 h-5 text-zinc-400 shrink-0" />
                <input
                  data-testid="learning-env-search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={learningReply?.check_id
                    ? "Answer the checking question in your own words."
                    : "Ask about your homework or what to practise next."}
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-500 text-white"
                  enterKeyHint="go"
                />
                <span className="w-px h-7 bg-zinc-700 shrink-0" />
                <button
                  type="button"
                  data-testid="learning-env-settings-btn"
                  aria-label="Preferences"
                  onClick={() => nav("/help/learner")}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-zinc-300 hover:bg-white/5"
                >
                  <SlidersHorizontal className="w-5 h-5" />
                </button>
              </div>
            </form>

            {learningError && <p role="alert">{learningError}</p>}

            {learningReply && (
              <section
                data-testid="evidence-led-learning-response"
                className="rounded-3xl border border-emerald-500/40 bg-zinc-900 p-5 text-left"
              >
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-300">
                  {learningReply.focus || "Learning support"}
                </p>
                {learningReply.answer && (
                  <p className="mt-3 text-sm leading-relaxed text-zinc-100">{learningReply.answer}</p>
                )}
                {learningReply.feedback && (
                  <p className="mt-3 text-sm leading-relaxed text-zinc-100">{learningReply.feedback}</p>
                )}
                {learningReply.check_question && (
                  <div className="mt-4 rounded-2xl bg-black/40 p-4">
                    <p className="text-xs font-bold text-amber-300">Check your understanding</p>
                    <p className="mt-1 text-sm text-zinc-200">{learningReply.check_question}</p>
                  </div>
                )}
                {learningReply.mode === "checked" && (
                  <p className="mt-4 text-xs text-zinc-400">
                    Your answer is saved for instructor review. Karen’s assessment is a suggestion, not confirmed understanding or practical progress.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setLearningReply(null)}
                  className="mt-4 text-xs font-bold text-zinc-400 hover:text-white"
                >
                  Close
                </button>
              </section>
            )}

            {/* Three intent circles — same destinations as home */}
            <div data-testid="learning-env-circles" className="grid grid-cols-3 gap-3 sm:gap-4">
              <SmallIntentCircle
                testid="learning-env-circle-progress"
                label="How am I doing?"
                sub="Track progress"
                Icon={Activity}
                ring="ring-sky-500/60"
                glow="shadow-[0_0_22px_-4px_rgba(56,189,248,0.55)]"
                iconClass="text-sky-400"
                onClick={() => nav("/learn/progress")}
              />
              <SmallIntentCircle
                testid="learning-env-circle-learning"
                label="Learning"
                sub="Continue learning"
                Icon={BookOpen}
                ring="ring-emerald-500/60"
                glow="shadow-[0_0_22px_-4px_rgba(52,211,153,0.55)]"
                iconClass="text-emerald-400"
                onClick={() => { /* already here */ }}
              />
              <SmallIntentCircle
                testid="learning-env-circle-booking"
                label="I want to make a booking"
                sub="Book a lesson"
                Icon={Calendar}
                ring="ring-fuchsia-500/60"
                glow="shadow-[0_0_22px_-4px_rgba(217,70,239,0.55)]"
                iconClass="text-fuchsia-400"
                onClick={() => nav("/learn/book")}
              />
            </div>
          </div>
        </section>

        {/* ── CURRENT FOCUS card ── */}
        <CurrentFocusCard />

        {/* ── What you'll learn later ── */}
        <section
          data-testid="learning-env-topics-ahead"
          className="rounded-3xl border border-zinc-800 bg-zinc-950/80 p-6"
        >
          <h2 className="text-xl sm:text-2xl font-extrabold">What you'll learn later</h2>
          <p className="mt-1 text-sm text-zinc-400">
            We'll guide you through everything you need to become a safe, confident driver.
          </p>
          {/* Containment doctrine (May 26, 2026): the 45-item
              topic grid is opt-in only. Default state is the
              calm reveal button below; tapping it expands the
              grid. The grid itself is purely reassurance — items
              are non-clickable bullet text. /learn/hub remains
              the destination for actual topic navigation, behind
              contextual slot-card chips. */}
          {!topicsAheadOpen && (
            <button
              type="button"
              data-testid="learning-env-topics-reveal-btn"
              onClick={() => setTopicsAheadOpen(true)}
              className="
                mt-5 inline-flex items-center gap-2 h-11 px-5
                rounded-2xl border border-emerald-500/40 bg-emerald-500/10
                text-sm font-extrabold text-emerald-300
                hover:bg-emerald-500/15 hover:border-emerald-400/60
                transition-colors
              "
            >
              Show me what's ahead
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {topicsAheadOpen && (
            <>
              <div
                data-testid="learning-env-topics-grid"
                className="mt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3"
              >
                {topicCols.map((col, ci) => (
                  <ul key={ci} className="space-y-2 text-sm text-zinc-300">
                    {col.map((topic) => (
                      <li key={topic} className="flex items-start gap-2">
                        <span className="mt-2 w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
              <button
                type="button"
                data-testid="learning-env-topics-hide-btn"
                onClick={() => setTopicsAheadOpen(false)}
                className="mt-5 inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-200"
              >
                Hide
              </button>
            </>
          )}
          <ReassuranceFooter />
        </section>

        {/* ── AI helper section — assistive, not demanding ── */}
        <AiHelpersSection nav={nav} />
      </main>

      {/* Understanding Card — operator-locked. Renders only when
          the search bar detected an understanding/support intent.
          Doctrine: acknowledge + analogy + real-life noticing +
          two calm choices. NEVER routes into formal progression. */}
      <UnderstandingCard
        open={!!understanding}
        topic={understanding?.topic}
        phrase={understanding?.phrase}
        onWantMore={() => {
          setUnderstanding(null);
          nav("/learn/overview");
        }}
        onClose={() => setUnderstanding(null)}
      />

      {/* Response-First Architecture V1 (Mar 1, 2026) — calm
          response surface for booking / homework / payment /
          progress / lesson-prep / instructor-connection / unknown.
          Navigation is ONLY fired by handleIntentChoice AFTER the
          learner clicks a choice whose kind === "navigate". */}
      <IntentResponseCard
        open={!!intentResponse}
        response={intentResponse}
        onChoice={handleIntentChoice}
        onClose={() => setIntentResponse(null)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────

function ExplainerCard({ testid, icon, title, body }) {
  return (
    <div
      data-testid={testid}
      className="flex-1 min-w-0 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-3"
    >
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-[11px] uppercase tracking-wider font-extrabold text-zinc-300">
          {title}
        </p>
      </div>
      <p className="mt-1 text-xs text-zinc-400 leading-snug">{body}</p>
    </div>
  );
}

function SmallIntentCircle({ testid, label, sub, Icon, ring, glow, iconClass, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`
        relative aspect-square w-full
        rounded-full bg-black ring-2 ${ring} ${glow}
        flex flex-col items-center justify-center text-center px-2
        transition-transform active:scale-95
        hover:shadow-[0_0_36px_-2px_rgba(255,255,255,0.18)]
      `}
    >
      <Icon className={`w-6 h-6 ${iconClass}`} strokeWidth={2.25} />
      <p className="mt-1.5 text-[10.5px] font-extrabold text-white leading-tight">{label}</p>
      <p className="mt-0.5 text-[9.5px] text-zinc-400 leading-tight">{sub}</p>
    </button>
  );
}

function CurrentFocusCard() {
  const nav = useNavigate();
  return (
    <section
      data-testid="learning-env-current-focus"
      className="rounded-3xl border border-emerald-700/50 bg-zinc-950/80 overflow-hidden"
    >
      <div className="grid md:grid-cols-2 gap-0">
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-emerald-400">
              Current focus
            </span>
            <span className="text-[10px] text-zinc-400 font-semibold">Lesson 1 of 8</span>
          </div>
          <h3 className="mt-4 text-2xl sm:text-3xl font-extrabold leading-tight">
            Getting to know the car and reverse parking.
          </h3>
          <p className="mt-3 text-sm text-zinc-400 max-w-md">
            Knowing your space. Learning slow is control.
          </p>

          <div className="mt-5">
            <div className="w-full h-2 rounded-full bg-zinc-800 overflow-hidden">
              <div
                data-testid="learning-env-current-focus-progress-bar"
                className="h-full bg-sky-500"
                style={{ width: "75%" }}
              />
            </div>
            <p className="mt-1.5 text-[11px] text-zinc-400 font-semibold">75% complete</p>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-testid="learning-env-continue-btn"
              onClick={() => nav("/learn")}
              className="h-11 px-5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-extrabold shadow-[0_0_24px_-6px_rgba(52,211,153,0.55)] transition-colors flex items-center justify-center gap-2"
            >
              Continue learning <ChevronRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="learning-env-review-btn"
              onClick={() => nav("/learn/ai-review-requests")}
              className="h-11 px-5 rounded-2xl border border-zinc-700 bg-zinc-900/60 text-sm font-extrabold text-white hover:border-zinc-500 transition-colors flex items-center justify-center gap-2"
            >
              <PlayCircle className="w-4 h-4" />
              Review last lesson
            </button>
          </div>
        </div>

        {/* Lesson visual + "In this lesson you will" */}
        <div className="bg-gradient-to-br from-zinc-900 to-black p-6 sm:p-8 flex flex-col justify-between">
          <div className="rounded-2xl bg-zinc-800/40 border border-zinc-700/40 h-32 sm:h-40 flex items-center justify-center text-zinc-500 text-xs">
            Lesson visual
          </div>
          <div className="mt-5">
            <p className="text-[11px] uppercase tracking-wider font-extrabold text-zinc-300">
              In this lesson you will:
            </p>
            <ul className="mt-2 space-y-1.5 text-sm text-zinc-300">
              {[
                "Understand the car controls",
                "Learn how to reverse safely",
                "Park with confidence",
              ].map((line) => (
                <li
                  key={line}
                  data-testid="learning-env-lesson-outcome"
                  className="flex items-start gap-2"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReassuranceFooter() {
  return (
    <div
      data-testid="learning-env-reassurance"
      className="mt-6 flex items-center gap-4 rounded-2xl border border-amber-500/30 bg-amber-500/[0.04] p-4"
    >
      <Mountain className="w-5 h-5 text-amber-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-200">
          <span className="font-extrabold text-amber-200">Don't worry about it all right now.</span>{" "}
          <span className="text-zinc-300">We'll give you one step at a time.</span>
        </p>
        <p className="mt-0.5 text-xs text-zinc-500">
          You're in control of your journey.
        </p>
      </div>
    </div>
  );
}

function AiHelpersSection({ nav }) {
  return (
    <section
      data-testid="learning-env-ai-helpers"
      className="rounded-3xl border border-sky-700/40 bg-sky-950/[0.15] p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-sky-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-extrabold">How can I help you today?</h3>
          <p className="text-xs text-zinc-400">
            I can review your driving, answer questions, or create a mock test for you.
          </p>
        </div>
      </div>
      <div
        data-testid="learning-env-ai-grid"
        className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
      >
        <AiHelperCard
          testid="learning-env-ai-video-review"
          icon={<PlayCircle className="w-4 h-4 text-sky-300" />}
          title="AI video review"
          body="Review your progress with AI video."
          onClick={() => nav("/learn/ai-review-requests")}
        />
        <AiHelperCard
          testid="learning-env-ai-analyse"
          icon={<Crosshair className="w-4 h-4 text-emerald-300" />}
          title="Analyse a specific part"
          body="Get feedback on a part of your driving."
          onClick={() => nav("/start-review")}
        />
        <AiHelperCard
          testid="learning-env-ai-mock-test"
          icon={<ClipboardList className="w-4 h-4 text-fuchsia-300" />}
          title="Mock test"
          body="Test your knowledge with a mock test."
          onClick={() => nav("/learn/highway-code")}
        />
      </div>
    </section>
  );
}

function AiHelperCard({ testid, icon, title, body, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="text-left rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 hover:border-zinc-600 transition-colors flex items-start gap-3"
    >
      <div className="shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-extrabold text-white">{title}</p>
        <p className="text-[11px] text-zinc-400 leading-snug mt-1">{body}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-500 mt-1 shrink-0" />
    </button>
  );
}

